// Coordinacion entre pestañas del mismo portal.
//
// Sirve para dos cosas:
//   - Que una pestaña que descubre una version nueva le avise a las demas sin
//     esperar a que cada una haga su propio poll.
//   - Que no queden pestañas viejas vivas cuando una ya se actualizo: es
//     exactamente el escenario del usuario (varias pestañas abiertas hace
//     dias).
//
// BroadcastChannel no existe en algunos contextos (Safari viejo, ciertos
// modos privados). El fallback por evento `storage` de localStorage funciona
// en todos lados y tiene la misma semantica: llega a las otras pestañas, no a
// la propia.

import type { RemoteStamp } from './types';

const NAME = 'authsys:version-guard';
const LS_KEY = 'authsys:version-guard:broadcast';

export interface UpdateMessage {
  type: 'update-available';
  portal: string;
  stamp: RemoteStamp;
  at: number;
}

type Handler = (msg: UpdateMessage) => void;

export function createChannel(portal: string, onMessage: Handler): {
  post: (stamp: RemoteStamp) => void;
  close: () => void;
} {
  let bc: BroadcastChannel | null = null;

  const dispatch = (raw: unknown): void => {
    if (typeof raw !== 'object' || raw === null) return;
    const msg = raw as UpdateMessage;
    if (msg.type !== 'update-available' || msg.portal !== portal) return;
    onMessage(msg);
  };

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel(NAME);
      bc.onmessage = (ev: MessageEvent) => dispatch(ev.data);
    }
  } catch {
    bc = null;
  }

  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== LS_KEY || !ev.newValue) return;
    try {
      dispatch(JSON.parse(ev.newValue));
    } catch {
      // mensaje corrupto — ignorar
    }
  };
  window.addEventListener('storage', onStorage);

  return {
    post(stamp: RemoteStamp) {
      const msg: UpdateMessage = { type: 'update-available', portal, stamp, at: Date.now() };
      try {
        bc?.postMessage(msg);
      } catch {
        // canal cerrado
      }
      try {
        // Siempre tambien por localStorage: cubre el caso de pestañas viejas
        // que cargaron una version del guard sin BroadcastChannel.
        window.localStorage.setItem(LS_KEY, JSON.stringify(msg));
      } catch {
        // storage bloqueado
      }
    },
    close() {
      window.removeEventListener('storage', onStorage);
      try {
        bc?.close();
      } catch {
        // ya cerrado
      }
    },
  };
}
